import numeral from '@tiger/numeral';
import { useSWRInfinite } from '@tiger/swr';
import { Table } from 'antd';
import { FC, useCallback, useMemo, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import LoadMore from 'components/LoadMore';
import ScrollTop from 'components/ScrollTop';
import security from 'stores/security';
import API from 'utils/API';
import classlist from 'utils/classlist';
import style from './style.scss';

const LIMIT = 20;

const StatusMap = {
    Succ: __('成功'),
    Fail: __('失败')
};

const emptyArr = [];

interface ColumnItemTypes {
    id: number;
    amount: number;
    createdAt: number;
    currency: string;
    fromBrId: number;
    toBrId: number;
    status: string;
}

const columns: Array<ColumnProps<ColumnItemTypes>> = [
    {
        title: __('Date'),
        dataIndex: 'createdAt',
        render: value => dayjs(value * 1000).format('YYYY/MM/DD'),
        showSorterTooltip: false
    },
    {
        title: __('internalTransfer:From'),
        dataIndex: 'fromBrId'
    },
    {
        title: __('internalTransfer:To'),
        dataIndex: 'toBrId'
    },
    {
        title: __('Amount'),
        dataIndex: 'amount',
        align: 'right',
        render: (value, record) => (
            <>
                <p className="mb-0">{numeral(value).format('0,0.00')}</p>
                <span className={style.currency}>{record.currency}</span>
            </>
        )
    },
    {
        title: __('Status'),
        dataIndex: 'status',
        align: 'right',
        width: 60,
        ellipsis: true,
        render: value => {
            const status = StatusMap[value] ? value.toLowerCase() : 'pending';
            const text = StatusMap[value] || __('处理中');

            return <span className={style[`status-${status}`]}>{text}</span>;
        }
    }
];

export interface LocationStateTypes {
    from_account_id: number;
    to_account_id: number;
}

const Record: FC = () => {
    const location = useLocation();
    const locationState = location.state as LocationStateTypes | null;
    const containerRef = useRef<HTMLDivElement>(null);

    const {
        data = emptyArr,
        error,
        isLoading,
        mutate,
        setSize
    } = useSWRInfinite<{ items: ColumnItemTypes[] }>(
        (pageIndex, pages) => {
            if (pages && pages.items.length === 0) {
                return null;
            }

            return (
                locationState && {
                    url: API.TRADE.deposit.getRecordList(),
                    params: {
                        start: pageIndex * LIMIT,
                        limit: LIMIT,
                        account_id: security.currentAccountId,
                        from_account_id: locationState.from_account_id,
                        to_account_id: locationState.to_account_id,
                        account_rel_type: 'INDIVIDUAL',
                        direction: 'OUT'
                    }
                }
            );
        },
        {
            dataChain: res => res.data
        }
    );

    const isEnd = useMemo(() => {
        const lastPageLength = data.lastItem?.items?.length || 0;

        return lastPageLength < LIMIT;
    }, [data]);

    const list = useMemo<ColumnItemTypes[]>(() => {
        if (!data) {
            return [];
        }

        return data
            .flat()
            .flatMap(item => item.items)
            .map(item => ({ ...item, key: item.id }));
    }, [data]);

    const loadMore = useCallback(() => {
        setSize(size => size + 1);
    }, [setSize]);

    const render = () => {
        if (isLoading) {
            return <Loading />;
        }

        if (error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (!list.length) {
            return <span className="tips">{__('No records')}</span>;
        }

        if (!locationState) {
            return <Navigate to="/deposit" />;
        }

        return (
            <div ref={containerRef}>
                <Table columns={columns} pagination={false} dataSource={list} />
                <LoadMore
                    isAuto
                    error={error}
                    loading={isLoading}
                    onScrollToBottom={loadMore}
                    container={containerRef.current!}
                    showMoreBtn={isEnd}
                    isEnd={isEnd}
                />
            </div>
        );
    };

    return (
        <div
            className={classlist(style.root, {
                [style.empty]: !data.length
            })}>
            <ScrollTop />
            {render()}
        </div>
    );
};

export default Record;
